<template>
  <div class="tiles-block flex flex-col space-y-4 bg-gray-50 text-gray-600">
    <div class="flex items-center space-x-6 text-gray-400">
      <tiles-layout-controls
        v-model="layoutConfig"
        :min-blocks="TILES_CONFIG.MIN_BLOCKS"
        :max-blocks="TILES_CONFIG.MAX_BLOCKS"
        :disabled="disabled"
        @update:model-value="handleLayoutChange"
      />
      <tiles-content-control
        v-if="layoutConfig.blockCount"
        v-model="contentConfig"
        :disabled="disabled"
        @update:model-value="handleContentChange"
      />
    </div>
    <div class="mt-6">
      <draggable
        v-model="tilesLocal"
        class="grid grid-cols-1 gap-4"
        ghost-class="ghost"
        item-key="title"
        handle=".drag-handle"
        :disabled="disabled"
        data-cy="tiles-draggable"
        @change="handleTilesReorder"
        @start="onDragStart"
        @end="onDragEnd"
      >
        <template #item="{ element, index }: { element: Tile; index: number }">
          <div class="draggable-item" :data-index="index">
            <block-card
              :title="
                $t('BORROWER_PLATFORM.TILES.CARD_TITLE', { index: index + 1 })
              "
              :data-cy="`tile-card-${index + 1}`"
              :show-delete="layoutConfig.blockCount > TILES_CONFIG.MIN_BLOCKS"
              :disabled="disabled"
              @delete="handleTileDelete(index)"
            >
              <tile-inputs
                :tile="element"
                :content-config="contentConfigForTileInputs"
                :template-id="templateId"
                :disabled="disabled"
                @tile:updated="
                  (updatedTile) => handleTileUpdate(index, updatedTile)
                "
              />
            </block-card>
          </div>
        </template>
      </draggable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from "vue";
import draggable from "vuedraggable";

import { useBlockEdit } from "@/composables/useBlockEdit";
import { HeadingLevel } from "@/enums/bpCustomisation";
import {
  BorrowerPlatformTileHeaderType,
  type TilesContentControlValue,
} from "@/enums/bpCustomisation";
import {
  BlockLayout,
  DimensionsMode,
  TileActionType,
  TileImageType,
} from "@/models/borrowerPlatformTemplates";

import BlockCard from "./Controlls/BlockCard.vue";
import TileInputs from "./Controlls/TileInputs.vue";
import TilesContentControl from "./Controlls/TilesContentControl.vue";
import TilesLayoutControls from "./Controlls/TilesLayoutControls.vue";

import type {
  Block,
  Tile,
  TilesBlockProps,
} from "@/models/borrowerPlatformTemplates";

// Helper function to safely convert HeadingLevel to BorrowerPlatformTileHeaderType
const mapHeadingLevelToTileHeader = (
  heading: HeadingLevel | undefined
): BorrowerPlatformTileHeaderType => {
  if (!heading) return BorrowerPlatformTileHeaderType.H1;

  const mapping: Record<HeadingLevel, BorrowerPlatformTileHeaderType> = {
    [HeadingLevel.H1]: BorrowerPlatformTileHeaderType.H1,
    [HeadingLevel.H2]: BorrowerPlatformTileHeaderType.H2,
    [HeadingLevel.H3]: BorrowerPlatformTileHeaderType.H3,
    [HeadingLevel.H4]: BorrowerPlatformTileHeaderType.H4,
  };

  return mapping[heading] ?? BorrowerPlatformTileHeaderType.H1;
};

const TILES_CONFIG = {
  MIN_BLOCKS: 0,
  MAX_BLOCKS: 6,
  DEFAULT_ICON: "default",
} as const;

const {
  modelValue: block,
  templateId,
  disabled = false,
} = defineProps<
  TilesBlockProps & {
    disabled?: boolean;
  }
>();

const { updateMultipleFields } = useBlockEdit(toRef(() => block));

interface DraggableChangeEvent {
  moved?: {
    element: Tile;
    newIndex: number;
    oldIndex: number;
  };
}

const isDragging = ref(false);

const createDefaultTile = (
  _index: number,
  contentConfigValue: TilesContentControlValue
): Tile => ({
  title: "",
  description: "",
  action: {
    type: contentConfigValue.button,
    text: "",
    url: null,
  },
  image: {
    type: contentConfigValue.image,
    icon: TILES_CONFIG.DEFAULT_ICON,
    file_id: undefined,
    dimensions: {
      mode: DimensionsMode.SCALE,
      scale: 100,
    },
  },
});

const generateTilesArray = (
  currentTiles: Tile[],
  targetCount: number,
  contentConfigValue: TilesContentControlValue
): Tile[] => {
  return Array.from(
    { length: targetCount },
    (_, i) => currentTiles[i] || createDefaultTile(i, contentConfigValue)
  );
};

const deriveContentConfig = (block: Block): TilesContentControlValue => {
  const firstTile = block.tiles?.[0];
  if (!firstTile) {
    return {
      image: TileImageType.NONE,
      header: BorrowerPlatformTileHeaderType.H1,
      button: TileActionType.NONE,
    };
  }

  return {
    image: firstTile.image.type,
    header: mapHeadingLevelToTileHeader(block.heading),
    button: firstTile.action.type,
  };
};

const layoutConfig = ref({
  layout: block.layout ?? BlockLayout.TWO_COLUMNS,
  blockCount: block.tiles?.length ?? 0,
});

const contentConfig = ref<TilesContentControlValue>(deriveContentConfig(block));

const contentConfigForTileInputs = computed(() => contentConfig.value);

const displayTiles = computed<Tile[]>(() => {
  const currentTiles = block.tiles || [];
  const targetCount = layoutConfig.value.blockCount;

  return generateTilesArray(currentTiles, targetCount, contentConfig.value);
});

const tilesLocal = ref<Tile[]>([]);

watch(
  displayTiles,
  (newTiles) => {
    tilesLocal.value = [...newTiles];
  },
  { immediate: true, deep: true }
);

const handleLayoutChange = (layout: {
  layout: BlockLayout;
  blockCount: number;
}) => {
  const tiles = generateTilesArray(
    block.tiles || [],
    layout.blockCount,
    contentConfig.value
  );

  updateMultipleFields({
    layout: layout.layout,
    tiles,
  });
};

const updateContentConfig = (content: TilesContentControlValue) => {
  contentConfig.value = content;
};

const updateTilesWithContentConfig = (content: {
  image: TileImageType;
  button: TileActionType;
}): Tile[] => {
  return displayTiles.value.map((tile) => ({
    ...tile,
    image: {
      type: content.image,
      icon:
        content.image === TileImageType.ILLUSTRATION
          ? tile.image.icon
          : undefined,
      file_id:
        content.image === TileImageType.IMAGE ? tile.image.file_id : undefined,
      dimensions: tile.image.dimensions || {
        mode: DimensionsMode.SCALE,
        scale: 100,
      },
    },
    action: {
      ...tile.action,
      type: content.button,
    },
  }));
};

const handleContentChange = (content: TilesContentControlValue) => {
  updateContentConfig(content);
  const updatedTiles = updateTilesWithContentConfig(content);

  updateMultipleFields({
    heading: content.header as unknown as HeadingLevel,
    tiles: updatedTiles,
  });
};

const handleTileUpdate = (index: number, updatedTile: Tile) => {
  const currentTiles = [...tilesLocal.value];
  currentTiles[index] = updatedTile;
  tilesLocal.value = currentTiles;

  updateMultipleFields({
    tiles: currentTiles,
  });
};

const handleTileDelete = (index: number) => {
  if (layoutConfig.value.blockCount <= TILES_CONFIG.MIN_BLOCKS || disabled) {
    return;
  }

  const currentTiles = [...tilesLocal.value];
  currentTiles.splice(index, 1);
  tilesLocal.value = currentTiles;

  layoutConfig.value.blockCount = currentTiles.length;

  updateMultipleFields({
    tiles: currentTiles,
  });
};

const onDragStart = () => {
  isDragging.value = true;
};

const onDragEnd = () => {
  isDragging.value = false;
};

const handleTilesReorder = (event: DraggableChangeEvent) => {
  if (!event.moved) {
    return;
  }

  updateMultipleFields({
    tiles: tilesLocal.value,
  });
};

watch(
  () => block,
  (newBlock, oldBlock) => {
    if (newBlock === oldBlock) {
      return;
    }

    layoutConfig.value = {
      layout: newBlock.layout ?? BlockLayout.TWO_COLUMNS,
      blockCount: newBlock.tiles?.length ?? 0,
    };

    contentConfig.value = deriveContentConfig(newBlock);
  },
  { immediate: true }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.ghost {
  @apply border-2 border-dashed border-blue-300 bg-blue-50 opacity-50;
}
</style>
